import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS } from "../constants/actions";
import { Loader2, Download, Search, X, ChevronDown, CheckSquare, Square, AlertTriangle, Mail, CalendarClock } from "lucide-react";
import { format, subDays } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import { SendEmailReportModal } from "../components/SendEmailReportModal";
import { ScheduleReportModal } from "../components/ScheduleReportModal";

/** Returns true if the hex color (6-char, no #) has low luminance (dark background). */
const isDarkColor = (hex: string): boolean => {
  if (hex.length !== 6) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};

/** Three-color CR coding: green ≥100%, yellow 90–99%, red <90% */
const applyCRThreeColorCoding = (ws: any) => {
  Object.keys(ws).forEach((ref) => {
    if (ref.startsWith("!")) return;
    const cell = ws[ref];
    if (cell && typeof cell.v === "string" && cell.v.endsWith("%")) {
      const pct = parseFloat(cell.v);
      if (isNaN(pct)) return;
      if (pct >= 100) {
        cell.s = { fill: { fgColor: { rgb: "C6EFCE" }, patternType: "solid" }, font: { color: { rgb: "276221" }, bold: true } };
      } else if (pct >= 90) {
        cell.s = { fill: { fgColor: { rgb: "FFEB9C" }, patternType: "solid" }, font: { color: { rgb: "9C6500" }, bold: true } };
      } else {
        cell.s = { fill: { fgColor: { rgb: "FFC7CE" }, patternType: "solid" }, font: { color: { rgb: "9C0006" }, bold: true } };
      }
    }
  });
};

interface RegionQueueRow {
  teamId: string;
  teamName: string;
  teamColor: string | null;
  queueId: string;
  queueName: string;
  metrics: {
    answeredCalls: number;
    missedCalls: number;
    abandonedCalls: number;
    voicemails: number;
    testCalls: number;
    grandTotal: number;
    captureRate: number;
    ahtMinutes: number;
    totalHandleMinutes: number;
    callbackCalls: number;
    callbackMissedCalls: number;
    outboundHandleMinutes: number;
    outboundConnectedCalls: number;
    outboundTotalCalls: number;
  };
}

interface RegionDashboard {
  regionId: string;
  regionName: string;
  rows: RegionQueueRow[];
}

// Default timezone for consistent display across all users
const DEFAULT_TIMEZONE = "America/Los_Angeles"; // Pacific Time

/**
 * Format a UTC date string to Pacific Time
 */
function formatInPacific(dateString: string, formatStr: string = "MMM d, yyyy"): string {
  const utcDate = new Date(dateString);
  const pacificDate = utcToZonedTime(utcDate, DEFAULT_TIMEZONE);
  return format(pacificDate, formatStr);
}

interface QueueMetrics {
  answeredCalls: number;
  missedCalls: number;
  abandonedCalls: number;
  voicemails: number;
  testCalls: number;
  grandTotal: number;
  captureRate: number;
  ahtMinutes: number;
  totalHandleMinutes: number;
  callbackCalls: number;
  callbackMissedCalls: number;
  outboundHandleMinutes: number;
  outboundConnectedCalls: number;
  outboundTotalCalls: number;
}

interface QueueRow {
  queueKey: string;
  queueName: string | null;
  metrics: QueueMetrics;
}

interface DateRange {
  start: string;
  end: string;
}

interface CustomerProfile {
  id: string;
  name: string;
  genesysQueueNames: string[];
}

interface CustomerSelectionState {
  [customerId: string]: boolean;
}

const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;

/**
 * Format capture rate for display.
 * Denominator matches the core-definitions CR formula: Total Calls Received minus
 * Abandoned minus Test. If that's 0 (nothing that counts toward capture rate), show
 * "N/A" instead of a misleading "0%".
 */
const formatCaptureRate = (metrics: QueueMetrics): string => {
  const denominator = metrics.grandTotal - metrics.abandonedCalls - metrics.testCalls;
  if (denominator <= 0) return "N/A";
  return formatPercent(metrics.captureRate);
};

const formatMinutes = (value: number) => value.toFixed(2);

export function CaptureRates() {
  const [queues, setQueues] = useState<QueueRow[]>([]);
  const [totals, setTotals] = useState<QueueMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [queriedDateRange, setQueriedDateRange] = useState<DateRange | null>(null);

  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 6), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [minDurationSeconds, setMinDurationSeconds] = useState<number>(0);

  // CR threshold filter
  const [crThreshold, setCrThreshold] = useState<number | null>(null);

  // Team member queue filter (always active)
  const [teamMemberNames, setTeamMemberNames] = useState<string[]>([]);

  useEffect(() => {
    fetch("/api/v1/team-members", {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (Array.isArray(json.data)) {
          setTeamMemberNames(
            json.data.map((m: any) => `${m.firstName} ${m.lastName}`.toLowerCase().trim())
          );
        }
      })
      .catch(() => {});
  }, []);

  // Customer selector state
  const [availableCustomers, setAvailableCustomers] = useState<CustomerProfile[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerSelectionState>({});
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerDropdownSearch, setCustomerDropdownSearch] = useState("");
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch available customers for the dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      const CACHE_KEY = "capture_rates_customers_cache";
      const CACHE_TTL = 30 * 60 * 1000;
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (
            Date.now() - timestamp < CACHE_TTL &&
            Array.isArray(data) &&
            data.every((c: any) => Array.isArray(c.genesysQueueNames))
          ) {
            setAvailableCustomers(data);
            return;
          }
        }
        const response = await fetch("/api/v1/capture-rates/customers-list", {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        });
        if (response.ok) {
          const json = await response.json();
          const customerList: CustomerProfile[] = Array.isArray(json.data) ? json.data : [];
          setAvailableCustomers(customerList);
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: customerList, timestamp: Date.now() }));
        }
      } catch {
        // silently fail — customer selector is optional
      }
    };
    fetchCustomers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getSelectedCustomerIds = useCallback(() => {
    return availableCustomers
      .filter((c) => selectedCustomers[c.id])
      .map((c) => c.id);
  }, [availableCustomers, selectedCustomers]);

  const handleToggleCustomer = (customerId: string) => {
    setSelectedCustomers((prev) => ({ ...prev, [customerId]: !prev[customerId] }));
  };

  const handleSelectAll = () => {
    const next: CustomerSelectionState = {};
    availableCustomers.forEach((c) => { next[c.id] = true; });
    setSelectedCustomers(next);
  };

  const handleSelectNone = () => setSelectedCustomers({});

  const selectedCount = Object.values(selectedCustomers).filter(Boolean).length;

  const filteredDropdownCustomers = useMemo(() => {
    if (!customerDropdownSearch.trim()) return availableCustomers;
    const s = customerDropdownSearch.toLowerCase();
    return availableCustomers.filter((c) => c.name.toLowerCase().includes(s));
  }, [availableCustomers, customerDropdownSearch]);

  // Per-customer aggregated CR computed from current queue data
  const customerCRSummary = useMemo(() => {
    if (availableCustomers.length === 0 || queues.length === 0) return [];
    return availableCustomers
      .map((customer) => {
        const queueNames = (customer.genesysQueueNames ?? []).map((n) => n.toLowerCase());
        const customerQueues = queues.filter((q) => {
          const qName = (q.queueName || q.queueKey).toLowerCase();
          return queueNames.some((n) => n === qName);
        });
        if (customerQueues.length === 0) return null;
        const agg = customerQueues.reduce(
          (acc, q) => ({
            answered: acc.answered + q.metrics.answeredCalls,
            missed: acc.missed + q.metrics.missedCalls,
            voicemails: acc.voicemails + q.metrics.voicemails,
            abandoned: acc.abandoned + q.metrics.abandonedCalls,
            testCalls: acc.testCalls + q.metrics.testCalls,
            grandTotal: acc.grandTotal + q.metrics.grandTotal,
            outboundTotalCalls: acc.outboundTotalCalls + (q.metrics.outboundTotalCalls ?? 0),
            callbacks: acc.callbacks + (q.metrics.callbackCalls ?? 0),
          }),
          { answered: 0, missed: 0, voicemails: 0, abandoned: 0, testCalls: 0, grandTotal: 0, outboundTotalCalls: 0, callbacks: 0 }
        );
        // Core-definitions formula: (Answered + Callbacks) / (Total Calls Received - Abandoned - Test)
        const denom = agg.grandTotal - agg.abandoned - agg.testCalls;
        const captureRatePct = denom > 0 ? ((agg.answered + agg.callbacks) / denom) * 100 : null;
        return { id: customer.id, name: customer.name, captureRatePct, ...agg, queueCount: customerQueues.length };
      })
      .filter(Boolean) as Array<{
        id: string; name: string; captureRatePct: number | null;
        answered: number; missed: number; voicemails: number; abandoned: number;
        testCalls: number; grandTotal: number; outboundTotalCalls: number; callbacks: number; queueCount: number;
      }>;
  }, [availableCustomers, queues]);

  const customersBelow = useMemo(() => {
    if (crThreshold === null) return [];
    return customerCRSummary
      .filter((c) => c.captureRatePct !== null && c.captureRatePct < crThreshold)
      .sort((a, b) => (a.captureRatePct ?? 0) - (b.captureRatePct ?? 0));
  }, [customerCRSummary, crThreshold]);

  // Queue table filtered by threshold and/or team member name
  const displayQueues = useMemo(() => {
    let result = queues;

    if (crThreshold !== null) {
      result = result.filter((q) => {
        const denom = q.metrics.grandTotal - q.metrics.abandonedCalls - q.metrics.testCalls;
        if (denom <= 0) return false;
        return q.metrics.captureRate * 100 < crThreshold;
      });
    }

    if (teamMemberNames.length > 0) {
      result = result.filter((q) => {
        const name = (q.queueName || q.queueKey || "").toLowerCase().trim();
        if (!name) return true;
        return !teamMemberNames.some(
          (memberName) => name.includes(memberName) || memberName.includes(name)
        );
      });
    }

    return result;
  }, [queues, crThreshold, teamMemberNames]);

  // CR queue table sort
  type CRSortCol = "queue" | "answered" | "missed" | "voicemails" | "abandoned" | "testCalls" | "total" | "captureRate" | "aht";
  const [crSortCol, setCrSortCol] = useState<CRSortCol | null>(null);
  const [crSortDir, setCrSortDir] = useState<"asc" | "desc">("desc");
  const handleCRSort = (col: CRSortCol) => {
    if (crSortCol === col) setCrSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setCrSortCol(col); setCrSortDir("desc"); }
  };

  const toFiniteNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sortedDisplayQueues = useMemo(() => {
    if (!crSortCol) return displayQueues;

    return [...displayQueues].sort((a, b) => {
      const aLabel = a.queueName || a.queueKey || "";
      const bLabel = b.queueName || b.queueKey || "";

      if (crSortCol === "queue") {
        const labelComparison = aLabel.localeCompare(bLabel, undefined, { sensitivity: "base", numeric: true });
        return crSortDir === "asc" ? labelComparison : -labelComparison;
      }

      const getNumericValue = (queue: QueueRow): number => {
        switch (crSortCol) {
          case "answered":    return toFiniteNumber(queue.metrics.answeredCalls);
          case "missed":      return toFiniteNumber(queue.metrics.missedCalls);
          case "voicemails":  return toFiniteNumber(queue.metrics.voicemails);
          case "abandoned":   return toFiniteNumber(queue.metrics.abandonedCalls);
          case "testCalls":   return toFiniteNumber(queue.metrics.testCalls);
          case "total":       return toFiniteNumber(queue.metrics.grandTotal);
          case "captureRate": return toFiniteNumber(queue.metrics.captureRate);
          case "aht":         return toFiniteNumber(queue.metrics.ahtMinutes);
          default:            return 0;
        }
      };

      const aValue = getNumericValue(a);
      const bValue = getNumericValue(b);
      const valueComparison = crSortDir === "asc" ? aValue - bValue : bValue - aValue;

      if (valueComparison !== 0) return valueComparison;

      return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base", numeric: true });
    });
  }, [displayQueues, crSortCol, crSortDir]);

  const [regionExportLoading, setRegionExportLoading] = useState(false);
  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [timeseriesInterval, setTimeseriesInterval] = useState<"daily" | "hourly">("daily");
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const canSendEmailReport = useAuthStore(
    (s) => s.user?.permissions?.includes(ACTION_KEYS.EMAIL_REPORT_SEND) ?? false
  );
  const [timeseriesDropdownOpen, setTimeseriesDropdownOpen] = useState(false);

  const downloadTimeseriesExcel = async (interval: "daily" | "hourly") => {
    setTimeseriesLoading(true);
    setTimeseriesDropdownOpen(false);
    try {
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime   = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams();
      params.append("startDate", startDateTime.toISOString());
      params.append("endDate",   endDateTime.toISOString());
      params.append("interval",  interval);
      params.append("timezone",  DEFAULT_TIMEZONE);
      if (minDurationSeconds > 0) params.append("minDurationSeconds", String(minDurationSeconds));
      getSelectedCustomerIds().forEach((id) => params.append("customerIds", id));

      const response = await fetch(`/api/v1/capture-rates/timeseries?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch timeseries data");
      const json = await response.json();

      const rows: Array<{
        period: string; queueName: string | null; queueKey: string;
        answeredCalls: number; missedCalls: number; abandonedCalls: number;
        voicemails: number; testCalls: number; grandTotal: number;
        captureRate: number; ahtMinutes: number; callbackCalls: number;
        outboundTotalCalls: number;
      }> = json.data?.rows || [];

      if (rows.length === 0) { toast.error("No data to download"); return; }

      const COLS = [
        interval === "hourly" ? "Date / Hour" : "Date",
        "Queue Name", "Answered", "Callbacks", "Missed", "Voicemails", "Abandoned", "Test Calls", "Total", "CR%", "AHT (min)",
      ];

      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2563EB" }, patternType: "solid" },
        alignment: { horizontal: "center" },
      };
      const subHeaderStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E5E7EB" }, patternType: "solid" },
      };
      const periodGroupStyle = {
        font: { bold: true, color: { rgb: "1E3A5F" } },
        fill: { fgColor: { rgb: "DBEAFE" }, patternType: "solid" },
      };

      const aoa: any[][] = [];

      // Title
      aoa.push([`Capture Rates — ${interval === "hourly" ? "Hourly" : "Daily"} Breakdown: ${startDate} to ${endDate}`, ...Array(COLS.length - 1).fill("")]);
      aoa.push([]);
      aoa.push([...COLS]);

      // Group rows by period so each date/hour block is visually clear
      const grouped = new Map<string, typeof rows>();
      for (const row of rows) {
        if (!grouped.has(row.period)) grouped.set(row.period, []);
        grouped.get(row.period)!.push(row);
      }

      for (const [period, periodRows] of grouped) {
        // Period header row
        aoa.push([period, ...Array(COLS.length - 1).fill("")]);

        for (const r of periodRows) {
          const cr = r.grandTotal > 0 ? `${(r.captureRate * 100).toFixed(1)}%` : "N/A";
          aoa.push([
            "",
            r.queueName || r.queueKey,
            r.answeredCalls,
            r.callbackCalls ?? 0,
            r.missedCalls,
            r.voicemails,
            r.abandonedCalls,
            r.testCalls,
            r.grandTotal,
            cr,
            r.ahtMinutes.toFixed(2),
          ]);
        }

        // Period subtotal
        const tot = periodRows.reduce((a, r) => ({
          answered:  a.answered  + r.answeredCalls,
          callbacks: a.callbacks + (r.callbackCalls ?? 0),
          missed:    a.missed    + r.missedCalls,
          voicemails: a.voicemails + r.voicemails,
          abandoned: a.abandoned + r.abandonedCalls,
          testCalls: a.testCalls + r.testCalls,
          grandTotal: a.grandTotal + r.grandTotal,
          handleMin: a.handleMin + r.ahtMinutes * r.answeredCalls,
        }), { answered: 0, callbacks: 0, missed: 0, voicemails: 0, abandoned: 0, testCalls: 0, grandTotal: 0, handleMin: 0 });

        const totCR = tot.grandTotal > 0 ? `${((tot.answered / tot.grandTotal) * 100).toFixed(1)}%` : "N/A";
        const totAHT = tot.answered > 0 ? (tot.handleMin / tot.answered).toFixed(2) : "0.00";
        aoa.push(["Subtotal", "", tot.answered, tot.callbacks, tot.missed, tot.voicemails, tot.abandoned, tot.testCalls, tot.grandTotal, totCR, totAHT]);
        aoa.push([]);
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Column widths
      ws["!cols"] = [{ wch: 26 }, { wch: 38 }, { wch: 10 }, { wch: 11 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 10 }];

      // Merge title row
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } }];

      // Apply styles
      const titleCell = ws["A1"];
      if (titleCell) titleCell.s = { font: { bold: true, sz: 13 }, alignment: { horizontal: "left" } };

      // Column header row (row index 2 = aoa[2])
      COLS.forEach((_, ci) => {
        const addr = XLSX.utils.encode_cell({ r: 2, c: ci });
        if (ws[addr]) ws[addr].s = headerStyle;
      });

      // Style period group rows and subtotal rows
      let rowIdx = 3;
      for (const [, periodRows] of grouped) {
        // Period header
        const periodCell = ws[XLSX.utils.encode_cell({ r: rowIdx, c: 0 })];
        if (periodCell) {
          periodCell.s = periodGroupStyle;
          // Merge across all cols
          if (!ws["!merges"]) ws["!merges"] = [];
          ws["!merges"].push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: COLS.length - 1 } });
        }
        rowIdx += periodRows.length + 1;

        // Subtotal row
        COLS.forEach((_, ci) => {
          const addr = XLSX.utils.encode_cell({ r: rowIdx, c: ci });
          if (ws[addr]) ws[addr].s = subHeaderStyle;
        });
        rowIdx += 2; // subtotal + blank
      }

      // CR% color coding
      applyCRThreeColorCoding(ws);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, interval === "hourly" ? "Hourly Breakdown" : "Daily Breakdown");
      XLSX.writeFile(wb, `capture-rates-${interval}_${startDate}_to_${endDate}.xlsx`);
      toast.success(`${interval === "hourly" ? "Hourly" : "Daily"} breakdown downloaded`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setTimeseriesLoading(false);
    }
  };

  const downloadRegionExcel = async () => {
    if (queues.length === 0) {
      toast.error("No data to download");
      return;
    }

    setRegionExportLoading(true);
    try {
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);
      const params = new URLSearchParams();
      params.append("startDate", startDateTime.toISOString());
      params.append("endDate", endDateTime.toISOString());
      if (minDurationSeconds > 0) params.append("minDurationSeconds", String(minDurationSeconds));
      getSelectedCustomerIds().forEach((id) => params.append("customerIds", id));

      const response = await fetch(`/api/v1/capture-rates/region-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!response.ok) throw new Error("Failed to load region data");
      const json = await response.json();
      const regions: RegionDashboard[] = json.data?.regions || [];

      if (regions.length === 0) {
        toast.error("No region data available. Make sure regions and teams are configured.");
        return;
      }

      const NUM_COLS = 9;
      const aoa: any[][] = [];
      const merges: XLSX.Range[] = [];
      const rowMeta: {
        type: "title" | "subtitle" | "regionHeader" | "colHeader" | "data" | "teamTotal" | "regionTotal" | "spacer";
        teamColor?: string;
      }[] = [];

      // ── Title block ────────────────────────────────────────────────────────
      aoa.push([`Capture Rates by Region`, ...Array(NUM_COLS - 1).fill("")]);
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: NUM_COLS - 1 } });
      rowMeta.push({ type: "title" });

      aoa.push([`${startDate}  →  ${endDate}`, ...Array(NUM_COLS - 1).fill("")]);
      merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: NUM_COLS - 1 } });
      rowMeta.push({ type: "subtitle" });

      aoa.push(Array(NUM_COLS).fill(""));
      rowMeta.push({ type: "spacer" });

      for (const region of regions) {
        // Group rows by team
        const teamGroups = new Map<string, { teamName: string; teamColor: string | null; rows: RegionQueueRow[] }>();
        for (const row of region.rows) {
          if (!teamGroups.has(row.teamId)) {
            teamGroups.set(row.teamId, { teamName: row.teamName, teamColor: row.teamColor, rows: [] });
          }
          teamGroups.get(row.teamId)!.rows.push(row);
        }

        // Region header
        const regionHeaderIdx = aoa.length;
        aoa.push([region.regionName.toUpperCase(), ...Array(NUM_COLS - 1).fill("")]);
        merges.push({ s: { r: regionHeaderIdx, c: 0 }, e: { r: regionHeaderIdx, c: NUM_COLS - 1 } });
        rowMeta.push({ type: "regionHeader" });

        // Column headers
        aoa.push(["Team", "Customer", "Answered", "Callbacks", "Missed", "Voicemails", "Total", "CR%", "AHT (min)"]);
        rowMeta.push({ type: "colHeader" });

        let regionAnswered = 0, regionCallbacks = 0, regionMissed = 0, regionVoicemails = 0, regionTotal = 0;
        let regionAbandoned = 0, regionTestCalls = 0;
        let regionHandleMinutes = 0, regionAnsweredForAHT = 0;
        let regionOutboundHandleMinutes = 0, regionOutboundConnected = 0;

        for (const [, team] of teamGroups) {
          const sortedRows = [...team.rows].sort((a, b) => a.queueName.localeCompare(b.queueName));
          const teamFirstRow = aoa.length;
          let teamAnswered = 0, teamCallbacks = 0, teamMissed = 0, teamVoicemails = 0, teamGrandTotal = 0;
          let teamAbandoned = 0, teamTestCalls = 0;
          let teamHandleMinutes = 0, teamAnsweredForAHT = 0;
          let teamOutboundHandleMinutes = 0, teamOutboundConnected = 0;

          for (let i = 0; i < sortedRows.length; i++) {
            const row = sortedRows[i];
            const denom = row.metrics.grandTotal - row.metrics.abandonedCalls - row.metrics.testCalls;
            const cr = denom > 0 ? `${(row.metrics.captureRate * 100).toFixed(1)}%` : "N/A";
            // Only write team name on first row of the group; merge cells below
            aoa.push([
              i === 0 ? team.teamName : "",
              row.queueName,
              row.metrics.answeredCalls,
              row.metrics.callbackCalls ?? 0,
              row.metrics.missedCalls,
              row.metrics.voicemails,
              row.metrics.grandTotal,
              cr,
              Number(row.metrics.ahtMinutes.toFixed(2)),
            ]);
            rowMeta.push({ type: "data", teamColor: team.teamColor || undefined });

            teamAnswered    += row.metrics.answeredCalls;
            teamCallbacks   += row.metrics.callbackCalls ?? 0;
            teamMissed      += row.metrics.missedCalls;
            teamVoicemails  += row.metrics.voicemails;
            teamGrandTotal  += row.metrics.grandTotal;
            teamAbandoned   += row.metrics.abandonedCalls;
            teamTestCalls   += row.metrics.testCalls;
            teamHandleMinutes   += row.metrics.totalHandleMinutes;
            teamAnsweredForAHT  += row.metrics.answeredCalls;
            teamOutboundHandleMinutes += row.metrics.outboundHandleMinutes ?? 0;
            teamOutboundConnected     += row.metrics.outboundConnectedCalls ?? 0;
          }

          // Merge team name cell vertically across all rows in this group
          if (sortedRows.length > 1) {
            merges.push({ s: { r: teamFirstRow, c: 0 }, e: { r: teamFirstRow + sortedRows.length - 1, c: 0 } });
          }

          // Team subtotal row — core-definitions formula: (Answered + Callbacks) / (Total - Abandoned - Test)
          const teamDenom = teamGrandTotal - teamAbandoned - teamTestCalls;
          const teamCR    = teamDenom > 0 ? `${(((teamAnswered + teamCallbacks) / teamDenom) * 100).toFixed(1)}%` : "N/A";
          const teamAhtDenom = teamAnsweredForAHT + teamOutboundConnected;
          const teamAHT   = teamAhtDenom > 0
            ? Number(((teamHandleMinutes + teamOutboundHandleMinutes) / teamAhtDenom).toFixed(2)) : 0;
          aoa.push([
            `${team.teamName} — Total`,
            "",
            teamAnswered, teamCallbacks, teamMissed, teamVoicemails, teamGrandTotal, teamCR, teamAHT,
          ]);
          merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: 1 } });
          rowMeta.push({ type: "teamTotal", teamColor: team.teamColor || undefined });

          regionAnswered      += teamAnswered;
          regionCallbacks     += teamCallbacks;
          regionMissed        += teamMissed;
          regionVoicemails    += teamVoicemails;
          regionTotal         += teamGrandTotal;
          regionAbandoned     += teamAbandoned;
          regionTestCalls     += teamTestCalls;
          regionHandleMinutes += teamHandleMinutes;
          regionAnsweredForAHT += teamAnsweredForAHT;
          regionOutboundHandleMinutes += teamOutboundHandleMinutes;
          regionOutboundConnected     += teamOutboundConnected;
        }

        // Region total row — core-definitions formula
        const regionDenom = regionTotal - regionAbandoned - regionTestCalls;
        const regionCR    = regionDenom > 0 ? `${(((regionAnswered + regionCallbacks) / regionDenom) * 100).toFixed(1)}%` : "N/A";
        const regionAhtDenom = regionAnsweredForAHT + regionOutboundConnected;
        const regionAHT   = regionAhtDenom > 0
          ? Number(((regionHandleMinutes + regionOutboundHandleMinutes) / regionAhtDenom).toFixed(2)) : 0;
        aoa.push(["Region Total", "", regionAnswered, regionCallbacks, regionMissed, regionVoicemails, regionTotal, regionCR, regionAHT]);
        merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: 1 } });
        rowMeta.push({ type: "regionTotal" });

        // Spacer between regions
        aoa.push(Array(NUM_COLS).fill(""));
        rowMeta.push({ type: "spacer" });
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = merges;
      ws["!cols"] = [
        { wch: 22 }, { wch: 34 }, { wch: 11 }, { wch: 11 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 11 },
      ];

      // Row heights
      ws["!rows"] = rowMeta.map((m) => {
        if (m.type === "title")       return { hpt: 28 };
        if (m.type === "subtitle")    return { hpt: 18 };
        if (m.type === "regionHeader") return { hpt: 22 };
        if (m.type === "colHeader")   return { hpt: 18 };
        if (m.type === "teamTotal")   return { hpt: 17 };
        if (m.type === "regionTotal") return { hpt: 20 };
        if (m.type === "spacer")      return { hpt: 6 };
        return { hpt: 17 }; // data rows
      });

      // Freeze panes below title + subtitle + spacer + regionHeader + colHeader = row index 5
      // (freeze is applied at the first region's column header, which is row index 4)
      ws["!freeze"] = { xSplit: 0, ySplit: 5 };

      const MEDIUM_BORDER = {
        top:    { style: "medium", color: { rgb: "CCCCCC" } },
        bottom: { style: "medium", color: { rgb: "CCCCCC" } },
        left:   { style: "thin",   color: { rgb: "CCCCCC" } },
        right:  { style: "thin",   color: { rgb: "CCCCCC" } },
      };

      // Apply styles row by row
      for (let r = 0; r < rowMeta.length; r++) {
        const meta = rowMeta[r];

        for (let c = 0; c < NUM_COLS; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { v: "", t: "s" };

          const centerAlign = { horizontal: "center" as const, vertical: "center" as const };
          const leftAlign   = { horizontal: "left" as const, vertical: "center" as const };

          if (meta.type === "title") {
            ws[ref].s = {
              fill: { fgColor: { rgb: "1F3864" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 16, name: "Calibri" },
              alignment: centerAlign,
            };
          } else if (meta.type === "subtitle") {
            ws[ref].s = {
              fill: { fgColor: { rgb: "2E5090" }, patternType: "solid" },
              font: { color: { rgb: "D9E2F3" }, sz: 11, name: "Calibri" },
              alignment: centerAlign,
            };
          } else if (meta.type === "regionHeader") {
            ws[ref].s = {
              fill: { fgColor: { rgb: "00B0F0" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 13, name: "Calibri" },
              alignment: centerAlign,
              border: MEDIUM_BORDER,
            };
          } else if (meta.type === "colHeader") {
            ws[ref].s = {
              fill: { fgColor: { rgb: "404040" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 10, name: "Calibri" },
              alignment: c <= 1 ? leftAlign : centerAlign,
              border: MEDIUM_BORDER,
            };
          } else if (meta.type === "data") {
            const rgb = (meta.teamColor || "FFFFFF").replace("#", "").toUpperCase().padStart(6, "0");
            const dark = isDarkColor(rgb);
            const fontColor = dark ? "FFFFFF" : "1A1A1A";
            ws[ref].s = {
              fill: { fgColor: { rgb }, patternType: "solid" },
              font: { color: { rgb: fontColor }, bold: c === 0, sz: 10, name: "Calibri" },
              alignment: c === 0
                ? { horizontal: "center" as const, vertical: "center" as const, wrapText: false }
                : c === 1
                ? leftAlign
                : centerAlign,
              border: {
                top:    { style: "thin", color: { rgb: dark ? "FFFFFF40" : "00000020" } },
                bottom: { style: "thin", color: { rgb: dark ? "FFFFFF40" : "00000020" } },
                left:   { style: "thin", color: { rgb: dark ? "FFFFFF40" : "00000020" } },
                right:  { style: "thin", color: { rgb: dark ? "FFFFFF40" : "00000020" } },
              },
            };
          } else if (meta.type === "teamTotal") {
            const rgb = (meta.teamColor || "E2E8F0").replace("#", "").toUpperCase().padStart(6, "0");
            const dark = isDarkColor(rgb);
            const fontColor = dark ? "FFFFFF" : "1A1A1A";
            ws[ref].s = {
              fill: { fgColor: { rgb }, patternType: "solid" },
              font: { color: { rgb: fontColor }, bold: true, sz: 10, italic: true, name: "Calibri" },
              alignment: c <= 1 ? leftAlign : centerAlign,
              border: {
                top:    { style: "medium", color: { rgb: dark ? "FFFFFF" : "888888" } },
                bottom: { style: "thin",   color: { rgb: dark ? "FFFFFF" : "888888" } },
                left:   { style: "thin",   color: { rgb: dark ? "FFFFFF" : "888888" } },
                right:  { style: "thin",   color: { rgb: dark ? "FFFFFF" : "888888" } },
              },
            };
          } else if (meta.type === "regionTotal") {
            ws[ref].s = {
              fill: { fgColor: { rgb: "1F3864" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11, name: "Calibri" },
              alignment: c <= 1 ? leftAlign : centerAlign,
              border: MEDIUM_BORDER,
            };
          } else if (meta.type === "spacer") {
            ws[ref].s = { fill: { fgColor: { rgb: "F8F9FA" }, patternType: "solid" } };
          }
        }
      }

      // CR% three-color coding (applied last so it wins over row background)
      applyCRThreeColorCoding(ws);

      // CR% column (col 6) — medium left/right borders to visually isolate it.
      // Also restores center alignment that three-color coding overwrites.
      const CR_COL_BORDER = { style: "medium" as const, color: { rgb: "444444" } };
      const THIN_SIDE     = { style: "thin"   as const, color: { rgb: "444444" } };
      for (let r = 0; r < rowMeta.length; r++) {
        const m = rowMeta[r];
        if (m.type === "spacer" || m.type === "title" || m.type === "subtitle") continue;
        const ref = XLSX.utils.encode_cell({ r, c: 6 });
        if (!ws[ref]) ws[ref] = { v: "", t: "s" };
        const existing = ws[ref].s || {};
        ws[ref].s = {
          ...existing,
          alignment: { horizontal: "center" as const, vertical: "center" as const },
          border: {
            top:    existing.border?.top    || THIN_SIDE,
            bottom: existing.border?.bottom || THIN_SIDE,
            left:   CR_COL_BORDER,
            right:  CR_COL_BORDER,
          },
        };
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "By Region");
      XLSX.writeFile(wb, `capture-rates-by-region_${startDate}_to_${endDate}.xlsx`);
      toast.success("Region export downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export region data");
    } finally {
      setRegionExportLoading(false);
    }
  };

  // Export low-CR customer report
  const downloadLowCRExcel = () => {
    if (crThreshold === null || customersBelow.length === 0) {
      toast.error("No customers below the selected threshold");
      return;
    }
    const aoa: any[][] = [];
    aoa.push([`Low Capture Rate Report — Below ${crThreshold}%`]);
    aoa.push([`Date Range: ${startDate} to ${endDate}`]);
    aoa.push([]);
    aoa.push(["Customer", "Capture Rate", "Answered", "Missed", "Voicemails", "Total", "Queues"]);
    for (const c of customersBelow) {
      aoa.push([
        c.name,
        c.captureRatePct !== null ? `${c.captureRatePct.toFixed(1)}%` : "N/A",
        c.answered,
        c.missed,
        c.voicemails,
        c.grandTotal,
        c.queueCount,
      ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 36 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 8 }];
    applyCRThreeColorCoding(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `CR Below ${crThreshold}%`);
    XLSX.writeFile(wb, `low-cr-report_below${crThreshold}pct_${startDate}_to_${endDate}.xlsx`);
    toast.success("Low CR report downloaded");
  };

  // Download Excel function
  const downloadExcel = () => {
    if (displayQueues.length === 0) {
      toast.error("No data to download");
      return;
    }

    const selectedCustomerList = availableCustomers.filter((c) => selectedCustomers[c.id]);
    const COLS = ["Queue Name", "Answered", "Missed", "Voicemails", "Abandoned", "Test Calls", "Total", "Capture Rate", "AHT (min)"];

    if (selectedCustomerList.length > 0) {
      // ── Grouped by customer export ──────────────────────────────────────
      const wb = XLSX.utils.book_new();

      // Main data sheet
      const aoa: any[][] = [];
      const merges: XLSX.Range[] = [];

      // Title row
      aoa.push([`Capture Rates: ${startDate} to ${endDate}`, ...Array(COLS.length - 1).fill("")]);
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } });
      aoa.push([]); // blank separator

      type SummaryEntry = {
        customer: string;
        queueMap: Map<string, {
          answered: number; missed: number; voicemails: number;
          abandoned: number; testCalls: number; total: number; callbacks: number;
          captureRate: string; aht: string;
        }>;
        totals: { answered: number; missed: number; voicemails: number; abandoned: number; testCalls: number; total: number; outboundTotal: number; callbacks: number };
      };

      const summaryEntries: SummaryEntry[] = [];
      const matchedQueueKeys = new Set<string>();

      for (const customer of selectedCustomerList) {
        const queueNames = (customer.genesysQueueNames ?? []).map((n) => n.toLowerCase());
        const customerQueues = displayQueues.filter((q) => {
          const qName = (q.queueName || q.queueKey).toLowerCase();
          return queueNames.some((n) => n === qName);
        });
        customerQueues.forEach((q) => matchedQueueKeys.add(q.queueKey));

        // Customer header row (merged)
        const customerHeaderRowIdx = aoa.length;
        aoa.push([`CUSTOMER: ${customer.name}`, ...Array(COLS.length - 1).fill("")]);
        merges.push({ s: { r: customerHeaderRowIdx, c: 0 }, e: { r: customerHeaderRowIdx, c: COLS.length - 1 } });

        // Column headers
        aoa.push([...COLS]);

        // Sort queues alphabetically
        const sortedQueues = [...customerQueues].sort((a, b) =>
          (a.queueName || a.queueKey).localeCompare(b.queueName || b.queueKey)
        );

        const queueMap = new Map<string, any>();
        let custAnswered = 0, custMissed = 0, custVoicemails = 0, custAbandoned = 0, custTestCalls = 0, custTotal = 0, custOutboundTotal = 0, custCallbacks = 0;

        for (const q of sortedQueues) {
          const cr = formatCaptureRate(q.metrics);
          const aht = q.metrics.ahtMinutes.toFixed(2);
          aoa.push([
            q.queueName || q.queueKey,
            q.metrics.answeredCalls,
            q.metrics.missedCalls,
            q.metrics.voicemails,
            q.metrics.abandonedCalls,
            q.metrics.testCalls,
            q.metrics.grandTotal,
            cr,
            aht,
          ]);
          queueMap.set(q.queueName || q.queueKey, {
            answered: q.metrics.answeredCalls,
            missed: q.metrics.missedCalls,
            voicemails: q.metrics.voicemails,
            abandoned: q.metrics.abandonedCalls,
            testCalls: q.metrics.testCalls,
            total: q.metrics.grandTotal,
            callbacks: q.metrics.callbackCalls ?? 0,
            captureRate: cr,
            aht,
          });
          custAnswered += q.metrics.answeredCalls;
          custMissed += q.metrics.missedCalls;
          custVoicemails += q.metrics.voicemails;
          custAbandoned += q.metrics.abandonedCalls;
          custTestCalls += q.metrics.testCalls;
          custTotal += q.metrics.grandTotal;
          custOutboundTotal += q.metrics.outboundTotalCalls || 0;
          custCallbacks += q.metrics.callbackCalls ?? 0;
        }

        // Customer subtotal row — core-definitions formula: (Answered + Callbacks) / (Total - Abandoned - Test)
        // custTotal includes outbound (Total column) — Capture Rate stays inbound-only.
        const custDenom = custTotal - custAbandoned - custTestCalls;
        const custCR = custDenom > 0 ? `${(((custAnswered + custCallbacks) / custDenom) * 100).toFixed(1)}%` : "N/A";
        aoa.push(["Customer Total", custAnswered, custMissed, custVoicemails, custAbandoned, custTestCalls, custTotal, custCR, ""]);
        aoa.push([]); // blank separator

        summaryEntries.push({
          customer: customer.name,
          queueMap,
          totals: { answered: custAnswered, missed: custMissed, voicemails: custVoicemails, abandoned: custAbandoned, testCalls: custTestCalls, total: custTotal, outboundTotal: custOutboundTotal, callbacks: custCallbacks },
        });
      }

      // Unmatched / unassigned queues
      const unmatchedQueues = displayQueues.filter((q) => !matchedQueueKeys.has(q.queueKey));
      if (unmatchedQueues.length > 0) {
        const unmatchedHeaderIdx = aoa.length;
        aoa.push(["OTHER QUEUES (Unassigned)", ...Array(COLS.length - 1).fill("")]);
        merges.push({ s: { r: unmatchedHeaderIdx, c: 0 }, e: { r: unmatchedHeaderIdx, c: COLS.length - 1 } });
        aoa.push([...COLS]);

        const sortedUnmatched = [...unmatchedQueues].sort((a, b) =>
          (a.queueName || a.queueKey).localeCompare(b.queueName || b.queueKey)
        );
        let uAnswered = 0, uMissed = 0, uVoicemails = 0, uAbandoned = 0, uTestCalls = 0, uTotal = 0, uCallbacks = 0;
        for (const q of sortedUnmatched) {
          aoa.push([
            q.queueName || q.queueKey,
            q.metrics.answeredCalls,
            q.metrics.missedCalls,
            q.metrics.voicemails,
            q.metrics.abandonedCalls,
            q.metrics.testCalls,
            q.metrics.grandTotal,
            formatCaptureRate(q.metrics),
            q.metrics.ahtMinutes.toFixed(2),
          ]);
          uAnswered += q.metrics.answeredCalls;
          uMissed += q.metrics.missedCalls;
          uVoicemails += q.metrics.voicemails;
          uAbandoned += q.metrics.abandonedCalls;
          uTestCalls += q.metrics.testCalls;
          uTotal += q.metrics.grandTotal;
          uCallbacks += q.metrics.callbackCalls ?? 0;
        }
        const uDenom = uTotal - uAbandoned - uTestCalls;
        const uCR = uDenom > 0 ? `${(((uAnswered + uCallbacks) / uDenom) * 100).toFixed(1)}%` : "N/A";
        aoa.push(["Subtotal", uAnswered, uMissed, uVoicemails, uAbandoned, uTestCalls, uTotal, uCR, ""]);
        aoa.push([]);
      }

      // Grand total row — core-definitions formula (totals.grandTotal includes outbound, denom stays inbound-only)
      if (totals) {
        const gtDenom = totals.grandTotal - totals.abandonedCalls - totals.testCalls;
        const gtCR = gtDenom > 0 ? `${(((totals.answeredCalls + (totals.callbackCalls ?? 0)) / gtDenom) * 100).toFixed(1)}%` : "N/A";
        aoa.push(["GRAND TOTAL", totals.answeredCalls, totals.missedCalls, totals.voicemails, totals.abandonedCalls, totals.testCalls, totals.grandTotal, gtCR, ""]);
      }

      const colWidths = [
        { wch: 36 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
        { wch: 11 }, { wch: 11 }, { wch: 10 }, { wch: 14 }, { wch: 10 },
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = merges;
      ws["!cols"] = colWidths;
      applyCRThreeColorCoding(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Capture Rates");

      // ── Summary sheet ───────────────────────────────────────────────────
      const summaryAoa: any[][] = [];
      const summaryMerges: XLSX.Range[] = [];

      summaryAoa.push([`Summary: ${startDate} to ${endDate}`, ...Array(COLS.length - 1).fill("")]);
      summaryMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } });
      summaryAoa.push([]);

      let grandAnswered = 0, grandMissed = 0, grandVoicemails = 0, grandAbandoned = 0, grandTestCalls = 0, grandTotalCalls = 0, grandCallbacks = 0;

      for (const entry of summaryEntries) {
        // Customer header (merged)
        const custHeaderIdx = summaryAoa.length;
        summaryAoa.push([`CUSTOMER: ${entry.customer}`, ...Array(COLS.length - 1).fill("")]);
        summaryMerges.push({ s: { r: custHeaderIdx, c: 0 }, e: { r: custHeaderIdx, c: COLS.length - 1 } });

        // Column headers
        summaryAoa.push([...COLS]);

        // Queue rows sorted alphabetically
        const sortedEntries = [...entry.queueMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));
        for (const [name, m] of sortedEntries) {
          summaryAoa.push([name, m.answered, m.missed, m.voicemails, m.abandoned, m.testCalls, m.total, m.captureRate, m.aht]);
        }

        // Customer total — core-definitions formula
        const { answered, missed, voicemails, abandoned, testCalls, total, callbacks } = entry.totals;
        const denom = total - abandoned - testCalls;
        const cr = denom > 0 ? `${(((answered + callbacks) / denom) * 100).toFixed(1)}%` : "N/A";
        summaryAoa.push(["Customer Total", answered, missed, voicemails, abandoned, testCalls, total, cr, ""]);
        summaryAoa.push([]);

        grandAnswered += answered;
        grandMissed += missed;
        grandVoicemails += voicemails;
        grandAbandoned += abandoned;
        grandTestCalls += testCalls;
        grandTotalCalls += total;
        grandCallbacks += callbacks;
      }

      // Grand total — core-definitions formula
      const grandDenom = grandTotalCalls - grandAbandoned - grandTestCalls;
      const grandCR = grandDenom > 0 ? `${(((grandAnswered + grandCallbacks) / grandDenom) * 100).toFixed(1)}%` : "N/A";
      summaryAoa.push(["GRAND TOTAL", grandAnswered, grandMissed, grandVoicemails, grandAbandoned, grandTestCalls, grandTotalCalls, grandCR, ""]);

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
      summaryWs["!merges"] = summaryMerges;
      summaryWs["!cols"] = colWidths;
      applyCRThreeColorCoding(summaryWs);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      const filename = `capture-rates_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success("Excel file downloaded");

    } else {
      // ── Flat export (no customers selected) ────────────────────────────
      const excelData = displayQueues.map((queue) => ({
        "Queue Name": queue.queueName || queue.queueKey,
        "Answered": queue.metrics.answeredCalls,
        "Missed": queue.metrics.missedCalls,
        "Voicemails": queue.metrics.voicemails,
        "Abandoned": queue.metrics.abandonedCalls,
        "Test Calls": queue.metrics.testCalls,
        "Total": queue.metrics.grandTotal,
        "Capture Rate": formatCaptureRate(queue.metrics),
        "AHT (min)": queue.metrics.ahtMinutes.toFixed(2),
      }));

      if (totals) {
        excelData.push({
          "Queue Name": "TOTAL",
          "Answered": totals.answeredCalls,
          "Missed": totals.missedCalls,
          "Voicemails": totals.voicemails,
          "Abandoned": totals.abandonedCalls,
          "Test Calls": totals.testCalls,
          "Total": totals.grandTotal,
          "Capture Rate": formatCaptureRate(totals),
          "AHT (min)": totals.ahtMinutes.toFixed(2),
        });
      }

      const ws = XLSX.utils.json_to_sheet(excelData);
      applyCRThreeColorCoding(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Capture Rates");
      const filename = `capture-rates_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success("Excel file downloaded");
    }
  };

  const fetchData = useCallback(async () => {
    // Validate date range - allow same day
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    setLoading(true);
    try {
      // Combine date and time in Pacific timezone for consistent results
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams();
      params.append("startDate", startDateTime.toISOString());
      params.append("endDate", endDateTime.toISOString());
      params.append("limit", "500");
      if (minDurationSeconds > 0) {
        params.append("minDurationSeconds", String(minDurationSeconds));
      }
      const selectedIds = getSelectedCustomerIds();
      selectedIds.forEach((id) => params.append("customerIds", id));

      const response = await fetch(`/api/v1/capture-rates/queues?${params}`);
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || "Failed to load capture rates");
      }

      const data = await response.json();
      setQueues(data.data?.queues || []);
      setTotals(data.data?.totals || null);
      setQueriedDateRange(data.data?.dateRange || null);
    } catch (error) {
      console.error("Failed to load capture rates", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to load capture rates"
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, startTime, endTime, minDurationSeconds, getSelectedCustomerIds]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setStartDate(format(subDays(new Date(), 6), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setStartTime("00:00");
    setEndTime("23:59");
    setMinDurationSeconds(0);
    setSelectedCustomers({});
    setCrThreshold(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Capture Rates</h1>
          <p className="mt-1 text-sm text-gray-500">
            View capture rates for all queues based on call history
          </p>
        </div>
        <div className="flex items-center gap-2">
          {crThreshold !== null && (
            <button
              onClick={downloadLowCRExcel}
              disabled={loading || customersBelow.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export Low CR
            </button>
          )}
          {/* Time Series export — split button with Daily / Hourly dropdown */}
          <div className="relative">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => downloadTimeseriesExcel(timeseriesInterval)}
                disabled={timeseriesLoading || loading || queues.length === 0}
                className="inline-flex items-center gap-2 rounded-l-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                {timeseriesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export {timeseriesInterval === "hourly" ? "Hourly" : "Daily"}
              </button>
              <button
                onClick={() => setTimeseriesDropdownOpen((o) => !o)}
                disabled={timeseriesLoading || loading || queues.length === 0}
                className="inline-flex items-center rounded-r-md border-l border-teal-500 bg-teal-600 px-2 py-2 text-white hover:bg-teal-700 disabled:opacity-50"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            {timeseriesDropdownOpen && (
              <div className="absolute right-0 z-10 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-lg">
                {(["daily", "hourly"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => { setTimeseriesInterval(opt); setTimeseriesDropdownOpen(false); downloadTimeseriesExcel(opt); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 capitalize ${timeseriesInterval === opt ? "font-semibold text-teal-700" : "text-gray-700"}`}
                  >
                    {opt === "daily" ? "Daily" : "Hourly"}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={downloadRegionExcel}
            disabled={loading || regionExportLoading || queues.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {regionExportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export by Region
          </button>
          <button
            onClick={downloadExcel}
            disabled={loading || queues.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export Excel
          </button>
          {canSendEmailReport && (
            <button
              onClick={() => setShowSendEmailModal(true)}
              disabled={loading || queues.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Mail className="h-4 w-4" />
              Send to Email
            </button>
          )}
          {canSendEmailReport && (
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <CalendarClock className="h-4 w-4" />
              Schedule
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-4 md:grid-cols-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Time
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={format(new Date(), "yyyy-MM-dd")}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Time
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Min Duration (sec)
            </label>
            <input
              type="number"
              min="0"
              value={minDurationSeconds}
              onChange={(e) =>
                setMinDurationSeconds(Math.max(0, parseInt(e.target.value) || 0))
              }
              placeholder="0"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Exclude calls shorter than this
            </p>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleClearFilters}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Customer Selector */}
      <div className="rounded-lg bg-white p-4 shadow space-y-4">
        <div className="relative" ref={customerDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Customer:
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setCustomerDropdownOpen(!customerDropdownOpen)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <div className="flex items-center justify-between">
                <span className="text-gray-700">
                  {selectedCount === 0
                    ? "All Customers"
                    : `${selectedCount} customer${selectedCount > 1 ? "s" : ""} selected`}
                </span>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    customerDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {customerDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
                <div className="p-2">
                  <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerDropdownSearch}
                      onChange={(e) => setCustomerDropdownSearch(e.target.value)}
                      className="flex-1 border-0 px-2 py-1 text-sm focus:outline-none"
                    />
                    {customerDropdownSearch && (
                      <button
                        onClick={() => setCustomerDropdownSearch("")}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="mb-2 flex gap-2 border-b border-gray-200 pb-2">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                    >
                      <CheckSquare className="h-3 w-3" />
                      Select All
                    </button>
                    <button
                      onClick={handleSelectNone}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-700"
                    >
                      <Square className="h-3 w-3" />
                      Select None
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredDropdownCustomers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No customers found
                      </div>
                    ) : (
                      filteredDropdownCustomers.map((customer) => (
                        <label
                          key={customer.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCustomers[customer.id] || false}
                            onChange={() => handleToggleCustomer(customer.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {customer.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {selectedCount > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {availableCustomers
                .filter((c) => selectedCustomers[c.id])
                .map((customer) => (
                  <span
                    key={customer.id}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
                  >
                    {customer.name}
                    <button
                      onClick={() => handleToggleCustomer(customer.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* CR Threshold Filter */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Show customers with CR below:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {[null, 90, 80, 70, 60, 50].map((threshold) => (
              <button
                key={threshold ?? "all"}
                onClick={() => setCrThreshold(threshold)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  crThreshold === threshold
                    ? threshold === null
                      ? "bg-gray-700 text-white"
                      : "bg-amber-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {threshold === null ? "All" : `< ${threshold}%`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {crThreshold !== null && customerCRSummary.length > 0 && (
              <span className="text-sm text-amber-600 font-medium">
                {customersBelow.length} of {customerCRSummary.length} customers below {crThreshold}%
              </span>
            )}
          </div>
        </div>

        {/* Customers below threshold table */}
        {crThreshold !== null && customersBelow.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-lg border border-amber-200">
            <table className="min-w-full divide-y divide-amber-100 text-sm">
              <thead className="bg-amber-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-amber-700">Customer</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Capture Rate</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Answered</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Missed</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Voicemails</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Total</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-amber-700">Queues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-50 bg-white">
                {customersBelow.map((c) => {
                  const pct = c.captureRatePct ?? 0;
                  const crColor = pct < 60 ? "text-red-600" : pct < 80 ? "text-orange-500" : "text-amber-600";
                  return (
                    <tr key={c.id} className="hover:bg-amber-50/50">
                      <td className="px-4 py-2 font-medium text-gray-900">{c.name}</td>
                      <td className={`px-4 py-2 text-right font-bold ${crColor}`}>
                        {c.captureRatePct !== null ? `${c.captureRatePct.toFixed(1)}%` : "N/A"}
                      </td>
                      <td className="px-4 py-2 text-right text-green-600">{c.answered.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-red-600">{c.missed.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-purple-600">{c.voicemails.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{c.grandTotal.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{c.queueCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {crThreshold !== null && customersBelow.length === 0 && customerCRSummary.length > 0 && (
          <p className="mt-3 text-sm text-green-600 font-medium">
            All customers are above {crThreshold}%
          </p>
        )}
      </div>

      {/* Totals Summary */}
      {totals && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Overall Summary
            </h2>
            {queriedDateRange && (
              <p className="text-sm text-gray-500">
                Data from: {formatInPacific(queriedDateRange.start)} to {formatInPacific(queriedDateRange.end)} (PT)
              </p>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-9">
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-sm text-green-600">Answered</p>
              <p className="text-2xl font-bold text-green-700">
                {totals.answeredCalls.toLocaleString()}
              </p>
              <p className="text-[10px] text-green-500 leading-tight">Raw count, excl. callbacks</p>
            </div>
            <div className="rounded-lg bg-teal-50 p-3">
              <p className="text-sm text-teal-600">Callbacks</p>
              <p className="text-2xl font-bold text-teal-700">
                {(totals.callbackCalls ?? 0).toLocaleString()}
              </p>
              <p className="text-[10px] text-teal-500 leading-tight">Counts as Answered in rate</p>
            </div>
            <div className="rounded-lg bg-red-50 p-3">
              <p className="text-sm text-red-600">Missed</p>
              <p className="text-2xl font-bold text-red-700">
                {totals.missedCalls.toLocaleString()}
              </p>
              <p className="text-[10px] text-red-500 leading-tight">Raw count, excl. callbacks</p>
            </div>
            <div className="rounded-lg bg-purple-50 p-3">
              <p className="text-sm text-purple-600">Voicemails</p>
              <p className="text-2xl font-bold text-purple-700">
                {totals.voicemails.toLocaleString()}
              </p>
              <p className="text-[10px] text-purple-500 leading-tight">Counted in rate total</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-sm text-gray-600">Abandoned</p>
              <p className="text-2xl font-bold text-gray-700">
                {totals.abandonedCalls.toLocaleString()}
              </p>
              <p className="text-[10px] text-gray-500 leading-tight">Excluded from rate</p>
            </div>
            <div className="rounded-lg bg-orange-50 p-3">
              <p className="text-sm text-orange-600">Test Calls</p>
              <p className="text-2xl font-bold text-orange-700">
                {totals.testCalls.toLocaleString()}
              </p>
              <p className="text-[10px] text-orange-500 leading-tight">Excluded from rate</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-sm text-blue-600">Total Calls Received</p>
              <p className="text-2xl font-bold text-blue-700">
                {totals.grandTotal.toLocaleString()}
              </p>
              <p className="text-[10px] text-blue-500 leading-tight">All calls, incl. abandoned &amp; test</p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3">
              <p className="text-sm text-emerald-600">Capture Rate</p>
              <p className="text-2xl font-bold text-emerald-700">
                {formatPercent(totals.captureRate)}
              </p>
              <p className="text-[10px] text-emerald-500 leading-tight">Answered incl. callbacks</p>
            </div>
            <div className="rounded-lg bg-sky-50 p-3">
              <p className="text-sm text-sky-600">Avg AHT</p>
              <p className="text-2xl font-bold text-sky-700">
                {totals.ahtMinutes > 0 ? `${totals.ahtMinutes.toFixed(1)}m` : "—"}
              </p>
              <p className="text-[10px] text-sky-500 leading-tight">Includes outbound calls</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading capture rates...</span>
        </div>
      )}

      {/* Queues Table */}
      {!loading && queues.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-gray-500">
            No call data found for the selected date range.
          </p>
        </div>
      )}

      {!loading && queues.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          {crThreshold !== null && (
            <div className="flex items-center justify-between border-b border-gray-200 bg-blue-50 px-4 py-2">
              <span className="text-sm font-medium text-blue-700">
                Showing {displayQueues.length} queue{displayQueues.length !== 1 ? "s" : ""}
                {crThreshold !== null && ` · CR below ${crThreshold}%`}
              </span>
              <button
                onClick={() => setCrThreshold(null)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Show all queues
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={() => handleCRSort("queue")} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Queue {crSortCol === "queue" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("answered")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Answered {crSortCol === "answered" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-teal-600">
                    Callbacks
                  </th>
                  <th onClick={() => handleCRSort("missed")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Missed {crSortCol === "missed" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("voicemails")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Voicemails {crSortCol === "voicemails" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("abandoned")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Abandoned {crSortCol === "abandoned" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("testCalls")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Test Calls {crSortCol === "testCalls" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("total")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Total {crSortCol === "total" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("captureRate")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Capture Rate {crSortCol === "captureRate" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handleCRSort("aht")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    AHT {crSortCol === "aht" ? (crSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedDisplayQueues.map((queue) => (
                  <tr key={`${queue.queueKey}::${queue.queueName ?? ""}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {queue.queueName || queue.queueKey}
                        </p>
                        {queue.queueName && (
                          <p className="text-xs text-gray-500">{queue.queueKey}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">
                      {queue.metrics.answeredCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-teal-600">
                      {(queue.metrics.callbackCalls ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">
                      {queue.metrics.missedCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-purple-600">
                      {queue.metrics.voicemails.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">
                      {queue.metrics.abandonedCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-orange-600">
                      {queue.metrics.testCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {queue.metrics.grandTotal.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {queue.metrics.grandTotal - queue.metrics.abandonedCalls - queue.metrics.testCalls <= 0 ? (
                        <span className="font-bold text-gray-400">N/A</span>
                      ) : (
                        <span
                          className={`font-bold ${
                            queue.metrics.captureRate >= 1.0
                              ? "text-green-600"
                              : queue.metrics.captureRate >= 0.9
                                ? "text-yellow-600"
                                : "text-red-600"
                          }`}
                        >
                          {formatPercent(queue.metrics.captureRate)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {formatMinutes(queue.metrics.ahtMinutes)} min
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showSendEmailModal && (
        <SendEmailReportModal
          reportType="capture_rates"
          availableCustomers={availableCustomers}
          defaultCustomerId={selectedCount === 1 ? getSelectedCustomerIds()[0] : undefined}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          minDurationSeconds={minDurationSeconds}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}
      {showScheduleModal && (
        <ScheduleReportModal
          reportType="capture_rates"
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </div>
  );
}
